/**
 * stripBonesFromSkin — the skin half of "Replace fingers" (2026-09-29): the
 * inverse of `bindGraftedDigits`. ONE responsibility: take named bones OUT of
 * every skinned mesh that deforms by them, give their flesh to one surviving
 * bone (the hand), and remove their nodes from the scene.
 *
 * THE FOLD. A vertex's weight on a removed bone moves to `into`; influences
 * that land on the same bone merge; the strongest four survive and are
 * renormalised (a merge can only free slots, so nothing is evicted unless the
 * vertex already had a fifth influence to lose — it cannot, three skins four).
 * Partition of unity is asserted, as the graft door asserts it.
 *
 * REST INVARIANCE by construction: at bind every bone's world · inverse-bind is
 * the identity, so moving a weight between bones moves no vertex at rest (the
 * graft door's argument, run backwards). In a pose the old fingers' flesh now
 * follows the hand — which is exactly the fingerless hand the graft then pins.
 *
 * THE SKELETON is rebuilt without the removed bones (their inverses dropped with
 * them), every skin index remapped, and each mesh rebound KEEPING its bindMatrix.
 * A removed bone's descendants that are not in the list are refused: a bone left
 * hanging off a node that no longer exists would be a rig/scene mismatch.
 *
 * Depends on: three. Depended on by: the run's hand pass (`nativeDigits: 'replace'`), tests.
 */
import { Skeleton, type Bone, type Object3D, type SkinnedMesh } from 'three';

export class StripBonesError extends Error {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot strip bones (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

export interface StripBonesReport {
  readonly bonesRemoved: readonly string[];
  readonly meshes: number;
  readonly verticesFolded: number;
  readonly worstWeightSumError: number;
}

export function stripBonesFromSkin(root: Object3D, remove: readonly string[], into: string): StripBonesReport {
  const gone = new Set(remove);
  const issues: string[] = [];
  const target = root.getObjectByName(into);
  if (target === undefined) issues.push(`the bone "${into}" that takes the flesh is not in the scene`);
  if (gone.has(into)) issues.push(`"${into}" cannot both take the flesh and be removed`);
  const nodes: Object3D[] = [];
  for (const name of remove) {
    const node = root.getObjectByName(name);
    if (node === undefined) issues.push(`no bone "${name}" in the scene`);
    else nodes.push(node);
  }
  // Every descendant of a removed node must itself be removed (or be a non-bone the removal carries).
  for (const node of nodes) {
    node.traverse((o) => {
      if (o !== node && (o as Bone).isBone === true && !gone.has(o.name)) issues.push(`"${o.name}" hangs off "${node.name}" and is not in the removal`);
    });
  }
  if (issues.length > 0) throw new StripBonesError(issues);

  const meshes: SkinnedMesh[] = [];
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if (m.isSkinnedMesh === true && m.skeleton.bones.some((b) => gone.has(b.name))) meshes.push(m);
  });
  let verticesFolded = 0;
  let worstWeightSumError = 0;
  for (const mesh of meshes) {
    const old = mesh.skeleton;
    const intoOld = old.bones.findIndex((b) => b.name === into);
    if (intoOld < 0) throw new StripBonesError([`mesh "${mesh.name}" deforms by the removed bones but its skeleton lacks "${into}"`]);
    const keep: number[] = [];
    old.bones.forEach((b, i) => {
      if (!gone.has(b.name)) keep.push(i);
    });
    const remap = new Map<number, number>();
    keep.forEach((oldIndex, newIndex) => remap.set(oldIndex, newIndex));
    const intoNew = remap.get(intoOld)!;
    const skinIndex = mesh.geometry.getAttribute('skinIndex');
    const skinWeight = mesh.geometry.getAttribute('skinWeight');
    const k4 = skinIndex.itemSize;
    for (let v = 0; v < skinIndex.count; v++) {
      const merged = new Map<number, number>();
      let folded = false;
      for (let k = 0; k < k4; k++) {
        const w = skinWeight.getComponent(v, k);
        const oi = skinIndex.getComponent(v, k);
        if (w <= 0) continue;
        const ni = remap.get(oi);
        const to = ni === undefined ? intoNew : ni;
        if (ni === undefined) folded = true;
        merged.set(to, (merged.get(to) ?? 0) + w);
      }
      if (folded) verticesFolded++;
      const entries = [...merged.entries()].sort((a, b) => b[1] - a[1]).slice(0, k4);
      const total = entries.reduce((s, [, w]) => s + w, 0);
      for (let k = 0; k < k4; k++) {
        const e = entries[k];
        skinIndex.setComponent(v, k, e === undefined ? 0 : e[0]);
        skinWeight.setComponent(v, k, e === undefined || total <= 0 ? 0 : e[1] / total);
      }
      if (total > 0) {
        let check = 0;
        for (let k = 0; k < k4; k++) check += skinWeight.getComponent(v, k);
        worstWeightSumError = Math.max(worstWeightSumError, Math.abs(check - 1));
      }
    }
    skinIndex.needsUpdate = true;
    skinWeight.needsUpdate = true;
    mesh.bind(new Skeleton(keep.map((i) => old.bones[i]!), keep.map((i) => old.boneInverses[i]!.clone())), mesh.bindMatrix.clone());
  }
  // The nodes leave the scene last: a removed chain's root is detached with everything under it.
  for (const node of nodes) if (node.parent !== null && !gone.has(node.parent.name)) node.parent.remove(node);
  return { bonesRemoved: [...remove], meshes: meshes.length, verticesFolded, worstWeightSumError };
}
